<script setup lang="ts">
import { computed } from "vue";
import SeverityBadge from "./SeverityBadge.vue";
import { eventFinalSeverity, triggerLabel, type QualityEvent, type Review } from "../api";

const props = withDefaults(
  defineProps<{
    review: Review;
    // Selection mode (manager bulk-delete). When `selectable`, an unapproved
    // row shows a checkbox and clicking the row toggles selection instead of
    // opening the drawer. Approved rows are never selectable (can't be deleted).
    selectable?: boolean;
    selected?: boolean;
    // Compact layout for the narrow pending-response Kanban columns. Instead of
    // reserving a left column for the severity badge, it stacks as a top row;
    // the title shrinks (full text on hover) and the per-QE decision summary is
    // dropped since it doesn't fit the column width.
    compact?: boolean;
  }>(),
  { selectable: false, selected: false, compact: false },
);
const emit = defineEmits<{
  (e: "open", reviewId: number): void;
  (e: "toggle-select", reviewId: number): void;
}>();

// A row participates in selection only when selection mode is on AND the review
// is still unapproved (deletion is unapproved-only).
const isSelectable = computed<boolean>(
  () => props.selectable && !props.review.approved_by,
);

// In selection mode a click toggles selection; otherwise it opens the drawer.
// (The row is a <button>, so we drive selection from its click rather than
// nesting a second interactive checkbox element inside it.)
function onRowClick(): void {
  if (isSelectable.value) emit("toggle-select", props.review.id);
  else emit("open", props.review.id);
}

function relTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const primaryLabel = computed<string>(() =>
  props.review.issue_title ||
  props.review.issue_code ||
  `review ${props.review.id}`,
);

const contextLabel = computed<string>(() => {
  const bits: string[] = [];
  if (props.review.repo) bits.push(props.review.repo);
  if (props.review.issue_code) bits.push(props.review.issue_code);
  if (props.review.pr_number) bits.push(`PR #${props.review.pr_number}`);
  if (props.review.commit_id) bits.push(props.review.commit_id.slice(0, 7));
  return bits.join(" · ");
});

// One-line decision summary, derived from the *visible* contributor rows.
// The serializer redacts rows the caller isn't entitled to see, so this
// naturally adapts: a contributor sees "you: agreed", a manager sees
// counts.
function decisionSummary(event: QualityEvent): string {
  const cs = event.contributors;
  if (cs.length === 0) return "no contributors";
  if (cs.length === 1) {
    const c = cs[0];
    const name = c.contributor.split("@")[0];
    if (c.contributor_decision === null) return `awaiting ${name}`;
    if (c.contributor_decision === "agree_with_comment") return `${name}: agreed (with comment)`;
    return `${name}: ${c.contributor_decision}`;
  }
  let agreed = 0;
  let pending = 0;
  let disputed = 0;
  for (const c of cs) {
    if (c.contributor_decision === null) pending++;
    else if (c.contributor_decision === "dispute") disputed++;
    else agreed++; // agree or agree_with_comment
  }
  const parts: string[] = [];
  if (agreed) parts.push(`${agreed} agreed`);
  if (pending) parts.push(`${pending} pending`);
  if (disputed) parts.push(`${disputed} disputed`);
  return parts.join(", ");
}

function statusSeverity(s: QualityEvent["status"]): "success" | "danger" | "secondary" {
  if (s === "closed") return "success";
  if (s === "disputed") return "danger";
  return "secondary";
}
</script>

<template>
  <button
    type="button"
    class="review-row"
    :class="{ selectable: isSelectable, selected: isSelectable && selected, compact }"
    :data-review-id="review.id"
    :data-testid="'review-row-' + review.id"
    :aria-pressed="isSelectable ? selected : undefined"
    @click="onRowClick"
  >
    <span
      v-if="isSelectable"
      class="row-checkbox"
      :class="{ checked: selected }"
      :data-testid="'review-select-' + review.id"
      aria-hidden="true"
    >
      <i v-if="selected" class="pi pi-check"></i>
    </span>
    <SeverityBadge
      v-if="!compact"
      :severity="review.highest_severity_event"
      :final-severity="review.final_highest_severity_event"
      class="row-sev"
    />
    <div class="row-main">
      <div v-if="compact" class="row-sev-line">
        <SeverityBadge
          :severity="review.highest_severity_event"
          :final-severity="review.final_highest_severity_event"
        />
      </div>
      <div class="row-title">
        <span class="row-id" data-testid="review-id-tag">R-{{ review.id }}</span>
        <span class="row-title-text" :title="compact ? primaryLabel : undefined">{{ primaryLabel }}</span>
      </div>
      <div class="row-context">
        <span class="badge-trigger" data-testid="trigger-tag">{{ triggerLabel(review.trigger_type) }}</span>
        <span v-if="contextLabel" class="row-where">{{ contextLabel }}</span>
        <span v-if="contextLabel" class="row-sep">·</span>
        <span class="row-time">{{ relTime(review.date) }}</span>
        <span v-if="!review.approved_by" class="badge-pending">
          pending approval
        </span>
        <span
          v-else-if="review.final_highest_severity_event !== null"
          class="badge-resolved"
          data-testid="resolved-tag"
        >
          resolved
        </span>
      </div>
      <ul v-if="review.events.length" class="event-summary-list">
        <li
          v-for="e in review.events"
          :key="e.id"
          class="event-summary-line"
          :data-event-id="e.id"
        >
          <SeverityBadge
            :severity="e.severity"
            :final-severity="eventFinalSeverity(e)"
            class="ev-sev"
          />
          <span class="ev-id" :data-testid="'qe-id-' + e.id">QE-{{ e.id }}</span>
          <span class="ev-summary">{{ e.summary }}</span>
          <span class="ev-status" :data-status="e.status">{{ e.status }}</span>
          <span v-if="!compact" class="ev-decisions">{{ decisionSummary(e) }}</span>
        </li>
      </ul>
    </div>
    <i class="pi pi-chevron-right row-chevron"></i>
  </button>
</template>

<style scoped>
.review-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: border-color 120ms, box-shadow 120ms, transform 80ms;
}
.review-row + .review-row { margin-top: 6px; }
.review-row:hover {
  border-color: var(--p-primary-400);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.review-row:active { transform: scale(0.998); }
.review-row:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}
.review-row.selected {
  border-color: var(--p-primary-500);
  background: var(--p-primary-50, #eff6ff);
}
.row-checkbox {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 1.5px solid var(--p-surface-400);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}
.row-checkbox.checked {
  background: var(--p-primary-500);
  border-color: var(--p-primary-500);
}
.row-checkbox .pi { font-size: 11px; color: white; }
.row-sev { flex-shrink: 0; margin-top: 2px; }
.row-main { flex: 1; min-width: 0; }
.row-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--p-surface-900);
  min-width: 0;
}
.row-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.row-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-100);
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.row-context {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 2px;
  font-size: 12px;
  flex-wrap: wrap;
  color: var(--p-surface-500);
}
.badge-trigger {
  background: var(--p-surface-100);
  color: var(--p-surface-600);
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
}
.row-where { color: var(--p-surface-500); }
.row-sep { color: var(--p-surface-400); }
.row-time { color: var(--p-surface-500); }
.badge-pending {
  background: #fed7aa;
  color: #7c2d12;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.badge-resolved {
  background: #d1fae5;
  color: #065f46;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.event-summary-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.event-summary-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--p-surface-700);
  padding-left: 6px;
  border-left: 2px solid var(--p-surface-200);
  min-width: 0;
}
.ev-sev :deep(.sev-badge) {
  font-size: 9px;
  padding: 1px 6px;
  letter-spacing: 0.03em;
}
.ev-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-100);
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.ev-summary {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--p-surface-800);
}
.ev-status {
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.04em;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  background: var(--p-surface-100);
  color: var(--p-surface-600);
}
.ev-status[data-status="closed"] { background: #d1fae5; color: #065f46; }
.ev-status[data-status="disputed"] { background: #fecaca; color: #7f1d1d; }
.ev-decisions {
  color: var(--p-surface-500);
  font-size: 11px;
  white-space: nowrap;
}
.row-chevron {
  color: var(--p-surface-400);
  font-size: 12px;
  margin-top: 4px;
}

/* Compact card for the narrow pending-response Kanban columns. The severity
   badge stacks as its own top row (no reserved left column), the title shrinks
   with its full text on hover, and the tighter padding fits the column width. */
.review-row.compact {
  padding: 10px 12px;
  gap: 0;
}
.row-sev-line {
  margin-bottom: 6px;
}
.review-row.compact .row-title {
  font-size: 12.5px;
}
</style>
